import { PlayerRoundScore, PlayerRoundState, Rarity, Zones } from '@alienheadwars/football-card-manager-model';
import { ContestResult } from '@alienheadwars/football-card-manager-model/dist/match/contest';
import { Box, Orientation } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { MediumCard, PitchDimensions } from '../../../../cards/playerCards/CardDimensions';
import { ExpandablePlayerCard } from '../../../../cards/playerCards/ExpandablePlayerCard';
import { rarityGlowColors } from '../../../../cards/playerCards/RarityGlow';
import { CounterComponent } from '../../../../counter/counter';
import { AnimatedBox, SpeedDurations } from '../../../../MotionMui/MotionMui';
import { BallComponent } from '../boardComponents/ball'; // Adjust paths as needed
import { ScoreBreakdown } from '../contestDetails/ScoreBreakdown';
import { MatchViewState, MatchViewStatesIndex, useMatchViewState } from '../matchViewContext/MatchViewContext';
import { PitchComponent } from './pitch';

//TODO

// Keep orientation default here for Board layout context
const defaultBoardOrientation: Orientation = 'horizontal';


interface BoardVisibilityState {
    homeTeam: string,
    attackingTeam: string,
    attackingHand: PlayerRoundState[],
    defendingHand: PlayerRoundState[],
    zone: Zones,
    attackingPlayer?: number,
    attackingScore?: PlayerRoundScore,
    defendingScore?: PlayerRoundScore,
    attackingTargetPlayer?: number,
    defendingPlayer?: number,
}

const setInitialState = (matchViewState: MatchViewState): BoardVisibilityState => {
    return {
        homeTeam: matchViewState.matchState.homeTeam,
        attackingTeam: matchViewState.currentRound.attackingTeam,
        attackingHand: matchViewState.currentRound.attackingHand,
        defendingHand: matchViewState.currentRound.defendingHand,
        zone: matchViewState.currentRound.zone,
        attackingPlayer: matchViewState.currentRound.attackingPlayer,
        attackingTargetPlayer: undefined,
        defendingPlayer: undefined,
        attackingScore: undefined,
        defendingScore: undefined,
    } as BoardVisibilityState;
}


export const BoardComponent = () => {

    const matchViewState = useMatchViewState()
    const [boardVisibilityState, setBoardVisibilityState] = React.useState<BoardVisibilityState>(setInitialState(matchViewState))

    const updateBoardVisibilityState = useCallback((newState: Partial<BoardVisibilityState>) => {
        setBoardVisibilityState(currentState => {
            return {
                ...currentState,
                ...newState,
            }
        })
    },
        [setBoardVisibilityState]
    )



    useEffect(() => {
        if (matchViewState.matchViewStatesIndex === 0) {
            updateBoardVisibilityState(setInitialState(matchViewState))
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SHOW_ATTACKING_PLAYER && matchViewState.currentRound.attackingPlayer !== undefined) {
            updateBoardVisibilityState({
                attackingPlayer: matchViewState.currentRound.attackingPlayer,
            },
            )
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SHOW_DEFENDING_PLAYER && matchViewState.currentRound.defendingPlayer !== undefined) {
            updateBoardVisibilityState({
                defendingPlayer: matchViewState.currentRound.defendingPlayer,
            },)
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SHOW_ATTACKING_TARGET_PLAYER && matchViewState.currentRound.attackingTargetPlayer !== undefined) {
            updateBoardVisibilityState({
                attackingTargetPlayer: matchViewState.currentRound.attackingTargetPlayer,
            })
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.FIRST_CONTEST_SCORES && matchViewState.currentRound.roundFinished) {
            updateBoardVisibilityState({
                attackingScore: matchViewState.currentRound.contests[0].attackingScore,
                defendingScore: matchViewState.currentRound.contests[0].defendingScore,
            },
            )
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.FIRST_CONTEST_LOSERS_DISCARD) {
            if (matchViewState.currentRound.contests[0].contestResult === ContestResult.ATTACKING_WON) {

                //If a second round is present, just update the defender in question
                //This logic is more complicated than I'd want, existing roundstate model doesn't easily support knowing who was discarded in which contest
                const updatedDefender = matchViewState.currentRound.defendingHand[matchViewState.currentRound.contests[0].defendingPlayerIndex]
                const updatedDefendingHand = boardVisibilityState.defendingHand.map((player) => {
                    if (player.lineupIndex === updatedDefender.lineupIndex) {
                        return updatedDefender
                    }
                    return player
                })
                updateBoardVisibilityState({
                    defendingHand: updatedDefendingHand,
                    defendingScore: undefined,
                },
                )
            }
            else {
                updateBoardVisibilityState({
                    attackingScore: undefined,
                    attackingHand: matchViewState.currentRound.attackingHand,
                },
                )

            }
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SECOND_CONTEST_BALL_ZONE) {
            updateBoardVisibilityState({
                zone: matchViewState.currentRound.contests[1].attackingZone,
            },
            )
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SECOND_CONTEST_DEFENDER) {
            updateBoardVisibilityState({
                defendingPlayer: matchViewState.currentRound.contests[1].defendingPlayerIndex,
            },
            )
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SECOND_CONTEST_SCORES) {
            updateBoardVisibilityState({
                attackingScore: matchViewState.currentRound.contests[1].attackingScore,
                defendingScore: matchViewState.currentRound.contests[1].defendingScore,
            },
            )
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.SECOND_CONTEST_LOSERS_DISCARD) {
            if (matchViewState.currentRound.contests[1].contestResult === ContestResult.ATTACKING_WON) {
                updateBoardVisibilityState({
                    defendingHand: matchViewState.currentRound.defendingHand,
                    defendingScore: undefined,
                },
                )
            }
            else if (matchViewState.currentRound.contests[1].contestResult === ContestResult.DRAW) {

                updateBoardVisibilityState({
                    attackingScore: undefined,
                    defendingScore: undefined,
                },
                )
            }
            else {
                updateBoardVisibilityState({
                    attackingHand: matchViewState.currentRound.attackingHand,
                    attackingScore: undefined,
                },
                )
            }
        }
        else if (matchViewState.matchViewStatesIndex === MatchViewStatesIndex.VICTORS_DISCARD) {
            updateBoardVisibilityState({
                attackingHand: matchViewState.currentRound.attackingHand,
                defendingHand: matchViewState.currentRound.defendingHand,
                attackingScore: undefined,
                defendingScore: undefined,
            },
            )
        }
    }, [
        boardVisibilityState.defendingHand,
        matchViewState.currentRound.attackingHand,
        matchViewState.currentRound.defendingHand,
        matchViewState.currentRound.attackingPlayer,
        matchViewState.currentRound.defendingPlayer,
        matchViewState.currentRound.attackingTargetPlayer,
        matchViewState.currentRound.zone,
        matchViewState.currentRound.contests,
        matchViewState.currentRound.roundFinished,
        matchViewState,
        updateBoardVisibilityState,
    ])


    return <Box
        sx={{
            position: 'relative', // Context for absolute positioning
        }}
    >
        {/* Render the Pitch Background */}
        <PitchComponent orientation={defaultBoardOrientation} />
        <BoardPieces
            {...boardVisibilityState}
        />
    </Box>
};

interface BoardVisibilityState {
    homeTeam: string,
    attackingTeam: string,
    attackingHand: PlayerRoundState[],
    defendingHand: PlayerRoundState[],
    zone: Zones,
    attackingPlayer?: number,
    attackingScore?: PlayerRoundScore,
    defendingScore?: PlayerRoundScore,
    attackingTargetPlayer?: number,
    defendingPlayer?: number,
}
interface BoardPiecesProps extends BoardVisibilityState {
}

const BoardPieces = (props: BoardPiecesProps) => {
    const { homeTeam, attackingTeam, attackingHand, defendingHand, zone, attackingPlayer, attackingTargetPlayer, defendingPlayer } = props;
    const homeIsAttacking = attackingTeam === homeTeam;
    const homeHand = homeIsAttacking ? attackingHand : defendingHand;
    const homePieces = homeHand.map((playerRoundState: PlayerRoundState) => <PlayerPiece
        playerRoundState={playerRoundState}
        homeTeam={true}
        homeIsAttacking={homeIsAttacking}
        zone={zone}
        positon={getPosition(homeIsAttacking, playerRoundState, attackingPlayer, defendingPlayer, attackingTargetPlayer)}
        attackingScore={props.attackingScore}
        defendingScore={props.defendingScore}
    />)
    const awayHand = homeIsAttacking ? defendingHand : attackingHand;
    const awayPieces = awayHand.map((playerRoundState: PlayerRoundState) => <PlayerPiece
        playerRoundState={playerRoundState}
        homeTeam={false}
        homeIsAttacking={homeIsAttacking}
        zone={zone}
        positon={getPosition(!homeIsAttacking, playerRoundState, attackingPlayer, defendingPlayer, attackingTargetPlayer)}
        attackingScore={props.attackingScore}
        defendingScore={props.defendingScore}
    />)

    const ballPiece = <BallPiece
        zone={zone}
        homeIsAtacking={homeIsAttacking}
    />

    const scores = <ScoreComponents {...props} />



    return [
        ...homePieces,
        ballPiece,
        ...awayPieces,
        scores,

    ]

}

enum PlayerPositionType {
    HAND,
    ATTACK,
    ATTACK_TARGET,
    DEFENSE,
}

const getPosition = (
    isAttacking: boolean,
    player: PlayerRoundState,
    attackingPlayer?: number,
    defendingPlayer?: number,
    attackingTargetPlayer?: number,
) => {
    return isAttacking ?
        (attackingPlayer === player.lineupIndex ? PlayerPositionType.ATTACK :
            attackingTargetPlayer === player.lineupIndex ? PlayerPositionType.ATTACK_TARGET :
                PlayerPositionType.HAND) :
        defendingPlayer === player.lineupIndex ? PlayerPositionType.DEFENSE : PlayerPositionType.HAND
}


interface PlayerPieceProps {
    playerRoundState: PlayerRoundState,
    homeTeam: boolean,
    //Derivable, but we have the information already so may as well pass through as this is an internal component
    homeIsAttacking: boolean,
    zone: Zones,
    positon: PlayerPositionType,
    attackingScore?: PlayerRoundScore,
    defendingScore?: PlayerRoundScore,
}

const playerPositions = {
    [Zones.ATTACK]: {
        [PlayerPositionType.ATTACK]: 4,
        [PlayerPositionType.ATTACK_TARGET]: 5,
        [PlayerPositionType.DEFENSE]: 6,
    },

    [Zones.MIDFIELD]: {
        [PlayerPositionType.ATTACK]: 2,
        [PlayerPositionType.ATTACK_TARGET]: 3,
        [PlayerPositionType.DEFENSE]: 4,
    },

    [Zones.DEFENSE]: {
        [PlayerPositionType.ATTACK]: 0,
        [PlayerPositionType.ATTACK_TARGET]: 1,
        [PlayerPositionType.DEFENSE]: 2,
    }
}
interface PlayerPieceVisibilityState {
    left: string,
    top: string,
    visibility: 'visible' | 'hidden',
    opacity: number,
}

export const PlayerPiece = (props: PlayerPieceProps) => {
    const { playerRoundState, homeTeam, homeIsAttacking, zone, positon } = props;
    const player = playerRoundState.playerDetails;

    const [playerVisibilityState, setPlayerVisibilityState] = React.useState<PlayerPieceVisibilityState>(undefined as unknown as PlayerPieceVisibilityState)

    useEffect(() => {
        if (playerRoundState.discarded) {
            setPlayerVisibilityState(currentState => {
                return {
                    ...currentState,
                    visibility: 'hidden',
                    opacity: 0,
                }
            })
        }
        else if (positon === PlayerPositionType.HAND) {
            setPlayerVisibilityState({
                left: convertIndexToPercent(3, homeIsAttacking),
                top: homeTeam ? '100%' : `-${pitchPortionWidthAsPercentage}`,
                visibility: 'hidden',
                opacity: 1,
            })
        }
        else {
            setPlayerVisibilityState({
                left: convertIndexToPercent(playerPositions[zone][positon], homeIsAttacking),
                top: positon === PlayerPositionType.ATTACK_TARGET ?
                    (homeTeam ? `${pitchPortionWidth * 2}%` : '0%') :
                    pitchPortionWidthAsPercentage,
                visibility: 'visible',
                opacity: 1,
            })
        }
    },
        [playerRoundState.discarded, homeIsAttacking, homeTeam, positon, zone]
    )
    return <AnimatedBox sx={{
        position: 'absolute',
        width: pitchPortionLengthAsPercentage,
        height: pitchPortionWidthAsPercentage,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        visibility: 'hidden',
        opacity: 0,
    }}
        animate={
            { ...playerVisibilityState }
        }
        speed='Slow'
    >
        <ExpandablePlayerCard
            dimensions={PitchDimensions}
            player={player}
            actions={(positon === PlayerPositionType.ATTACK && props.attackingScore) ?
                <ScoreBreakdown player={player} playerScore={props.attackingScore} dimensions={MediumCard} />
                : (positon === PlayerPositionType.DEFENSE && props.defendingScore) ?
                    <ScoreBreakdown player={player} playerScore={props.defendingScore} dimensions={MediumCard} />
                    : undefined
            }
        />
    </AnimatedBox>
}


const ScoreComponents = (props: BoardPiecesProps) => {

    const [attackingScore, setAttackingScore] = React.useState<PlayerRoundScore | undefined>(props.attackingScore)
    const [fadeAttacking, setFadeAttacking] = React.useState(false)

    const [defendingScore, setDefendingScore] = React.useState<PlayerRoundScore | undefined>(props.defendingScore)
    const [fadeDefending, setFadeDefending] = React.useState(false)

    useEffect(() => {
        if (props.attackingScore === undefined) {
            setFadeAttacking(true)
            setTimeout(() => {
                setAttackingScore(undefined)
            }, SpeedDurations.Normal * 1000)
        }
        else {
            setAttackingScore(props.attackingScore)
            setFadeAttacking(false)
        }
    }, [props.attackingScore])

    useEffect(() => {
        if (props.defendingScore === undefined) {
            setFadeDefending(true)
            setTimeout(() => {
                setDefendingScore(undefined)
            }, SpeedDurations.Normal * 1000)
        }
        else {
            setDefendingScore(props.defendingScore)
            setFadeDefending(false)
        }
    }, [props.defendingScore])


    return <>{attackingScore && <AnimatedBox sx={{
        position: 'absolute',
        left: convertIndexToPercent(playerPositions[props.zone][PlayerPositionType.ATTACK], props.attackingTeam === props.homeTeam),
        top: pitchPortionWidthAsPercentage,
        width: pitchPortionLengthAsPercentage,
        height: pitchPortionWidthAsPercentage,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        zIndex: 1000,
        pointerEvents: 'none',
    }
    }
        animate={{
            opacity: fadeAttacking ? 0 : 1,
        }}
        speed={'Slow'}
    >
        <ContestScore score={attackingScore} />
    </AnimatedBox >}
        {defendingScore && <AnimatedBox sx={{
            position: 'absolute',
            left: convertIndexToPercent(playerPositions[props.zone][PlayerPositionType.DEFENSE], props.attackingTeam === props.homeTeam),
            top: pitchPortionWidthAsPercentage,
            width: pitchPortionLengthAsPercentage,
            height: pitchPortionWidthAsPercentage,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            zIndex: 1000,
            pointerEvents: 'none',
        }}
            animate={{
                opacity: fadeDefending ? 0 : 1,
            }}
            speed={'Slow'}
        >
            <ContestScore score={defendingScore} />
        </AnimatedBox>
        }
    </>
}



interface BallPieceProps {
    zone: Zones
    homeIsAtacking: boolean,
}

const ballPositions = {
    [Zones.ATTACK]: 5,
    [Zones.MIDFIELD]: 3,
    [Zones.DEFENSE]: 1,
}

const pitchPortionLength = 100 / 7
const pitchPortionLengthAsPercentage = `${pitchPortionLength}%`

const pitchPortionWidth = 100 / 3
const pitchPortionWidthAsPercentage = `${pitchPortionWidth}%`

const convertIndexToPercent = (index: number, homeIsAtacking: boolean) => {
    const basePercent = pitchPortionLength * index
    const actualPercent = homeIsAtacking ? basePercent : (100 - (basePercent + pitchPortionLength))
    return `${actualPercent}%`
}

export const BallPiece = (props: BallPieceProps) => {
    const { zone, homeIsAtacking } = props;

    const leftPosition = convertIndexToPercent(ballPositions[zone], homeIsAtacking);


    return <AnimatedBox sx={{
        position: 'absolute',
        top: pitchPortionWidthAsPercentage,
        width: pitchPortionLengthAsPercentage,
        height: pitchPortionWidthAsPercentage,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    }}

        animate={{
            left: leftPosition,
            // visibility: 'visible',
            opacity: 1,
        }}
        speed={'Normal'}>
        <BallComponent direction={
            homeIsAtacking ? 'right' : 'left'
        } />
    </AnimatedBox>
}

interface ContestScoreProps {
    score: PlayerRoundScore
}


const ContestScore = (props: ContestScoreProps) => {
    const { score } = props
    const duration = ((500 + (300 * Math.random())) / Math.min(40, props.score.score)) * props.score.score

    return <CounterComponent
        initialValue={0}
        targetNumber={score.score}
        animationTime={duration}
        fontSize={PitchDimensions.playerNameFontSize}
        fontOutlineSize={PitchDimensions.heroIconOutlineWidth}
        minBoundaryNumber={0}
        maxBoundaryNumber={55}
        sizingMagnitude={0.5}
        minDigits={2}
        outlineColour={(newScore) => (newScore >= 55 ? rarityGlowColors[Rarity['GOD-TIER']] :
            newScore >= 50 ? rarityGlowColors[Rarity.LEGENDARY] :
                newScore >= 45 ? rarityGlowColors[Rarity.EPIC] :
                    newScore >= 35 ? rarityGlowColors[Rarity.RARE] :
                        newScore >= 30 ? rarityGlowColors[Rarity.UNCOMMON] :
                            rarityGlowColors[Rarity.COMMON]) ?? 'white'}
    />
}